import CloseIcon from '@/components/Icon/CloseIcon';
import IconButton from '@/components/IconButton/IconButton';
import { Video as VideoType } from '@/lib/apiTypes';
import cn from '@/lib/cs';
import { Dialog, DialogTitle } from '@headlessui/react';
import { AudioDescriptionVideoPlayerRef } from '@ntrinternet/ntr-ui';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import VideoPlayer from './VideoPlayer';

const VideoDialog: FC<{
	video: VideoType;
	isVideoOpened: boolean;
	setIsVideoOpened: (isVideoOpened: boolean) => void;
}> = ({ video, isVideoOpened, setIsVideoOpened }) => {
	const videoContainerRef = useRef<HTMLDivElement>(null);
	const videoPlayerRef = useRef<AudioDescriptionVideoPlayerRef>(null);

	const [isPlaying, setIsPlaying] = useState(true);
	const [videoDurationInSeconds, setVideoDurationInSeconds] = useState(0);
	const [currentVideoTimeInSeconds, setCurrentVideoTimeInSeconds] =
		useState(0);

	// Format video times for UI
	const formattedVideoTimes: {
		duration: string;
		currentTime: string;
	} = useMemo(() => {
		const formatTime = (timeInSeconds: number) => {
			const minutes = Math.floor(timeInSeconds / 60);
			const seconds = Math.floor(timeInSeconds % 60);
			return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
		};

		return {
			duration: formatTime(videoDurationInSeconds),
			currentTime: formatTime(currentVideoTimeInSeconds),
		};
	}, [videoDurationInSeconds, currentVideoTimeInSeconds]);

	// Enter fullscreen while playing and exit when paused
	useEffect(() => {
		try {
			if (isVideoOpened && isPlaying) {
				const enterFunc =
					videoContainerRef.current?.requestFullscreen ||
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(videoContainerRef.current as any)?.webkitRequestFullscreen;
				const requestFullscreen = enterFunc.bind(
					videoContainerRef.current,
				);
				requestFullscreen();
			} else {
				const element =
					document.fullscreenElement ||
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(document as any).webkitFullscreenElement;
				if (element) {
					const exitFunc =
						document.exitFullscreen ||
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						(document as any).webkitExitFullscreen;
					const exitFullscreen = exitFunc.bind(document);
					exitFullscreen();
				}
			}
		} catch (e) {
			console.error('Fullscreen error', e);
		}
	}, [isVideoOpened, isPlaying]);

	return (
		<>
			<Dialog
				open={isVideoOpened}
				onClose={() => setIsVideoOpened(false)}
				className="fixed inset-0 animate-fade-in bg-black">
				<DialogTitle className="sr-only">
					Video: {video.title}
				</DialogTitle>
				<div
					ref={videoContainerRef}
					className="relative h-full min-h-0 grow">
					<VideoPlayer
						ref={videoPlayerRef}
						isAutoPlay
						video={video}
						onIsPlayingChange={setIsPlaying}
						onCurrentTimeChange={(currentTime, duration) => {
							setCurrentVideoTimeInSeconds(currentTime);
							setVideoDurationInSeconds(duration);
						}}
						onEnded={() => setIsVideoOpened(false)}
					/>
					{!isPlaying && (
						<IconButton
							icon={<CloseIcon />}
							aria-label="Sluit video"
							title="Sluit video"
							className="absolute start-2 top-2"
							onClick={() => setIsVideoOpened(false)}
						/>
					)}
				</div>
				{videoDurationInSeconds > 0 && (
					<div
						className={cn(
							'fixed bottom-1 flex shrink-0 gap-2 px-4 py-2 text-white bg-black/60 backdrop-blur-md inset-x-1 transition-all rounded-full',
							isPlaying && 'opacity-0 translate-y-12',
							!isPlaying && 'opacity-100 translate-y-0',
						)}
						aria-hidden={isPlaying}>
						<time
							className="w-24 text-center"
							aria-label={`Huidige tijd: ${formattedVideoTimes.currentTime}`}>
							{formattedVideoTimes.currentTime}
						</time>
						<input
							type="range"
							className="w-full accent-white/90"
							disabled={isPlaying}
							min={0}
							max={videoDurationInSeconds}
							value={currentVideoTimeInSeconds}
							aria-label="Videotijd"
							aria-valuemin={0}
							aria-valuemax={videoDurationInSeconds}
							aria-valuenow={currentVideoTimeInSeconds}
							aria-valuetext={formattedVideoTimes.currentTime}
							onChange={(e) => {
								const newTime = parseInt(e.target.value);
								if (isNaN(newTime)) return;
								videoPlayerRef.current?.manuallyUpdateCurrentTime(
									newTime,
								);
								setCurrentVideoTimeInSeconds(newTime);
							}}
						/>
						<time
							className="w-24 text-center"
							aria-label={`Videoduur: ${formattedVideoTimes.duration}`}>
							{formattedVideoTimes.duration}
						</time>
					</div>
				)}
			</Dialog>
		</>
	);
};

export default VideoDialog;
